import { useEffect } from 'react';
import { Spinner, Stack, Typography } from '@prenuvo/halo';

import { useIntersectionObserver } from '@/shared/hook';
import { Loading } from '@/shared/ui';
import { isEmpty } from '@/shared/util';

import { IGNORED_FIELDS } from './activity-log.constant';
import { ActivityLogContent } from './activity-log-content';
import { ActivityLogHeader } from './activity-log-header.component';
import { useGetActivityLogs } from './use-get-activity-logs.hook';

const OBSERVER_OPTIONS: IntersectionObserverInit = { threshold: 1 };

export function ActivityLogsState() {
  const { isIntersecting, ref: fetchRef } = useIntersectionObserver(OBSERVER_OPTIONS);

  const { fetchNextPage, hasNextPage, isFetchingNextPage, items, status } = useGetActivityLogs();

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isIntersecting]);

  if (status === 'pending') {
    return <Loading />;
  }

  if (isEmpty(items)) {
    return (
      <Stack className="rounded-xl border p-20" justification="center">
        <Typography translationKey="FCT.ACTIVITY_LOGS.EMPTY" />
      </Stack>
    );
  }

  return (
    <>
      {items.map((activityLog) => {
        const allFieldsMatch =
          activityLog?.objectDiff &&
          Object.keys(activityLog.objectDiff).every((key) => IGNORED_FIELDS.includes(key));

        if (allFieldsMatch) {
          return null;
        }

        return (
          <div key={activityLog.uuid} className="pb-5">
            <ActivityLogHeader actor={activityLog.actor} timestamp={activityLog.timestamp} />
            <ActivityLogContent activityLog={activityLog} />
          </div>
        );
      })}

      <div className="h-px" ref={fetchRef} />

      {isFetchingNextPage && (
        <Stack className="p-20" justification="center">
          <Spinner size="sm" variant="white" />
        </Stack>
      )}

      {!hasNextPage && (
        <Stack className="border-t p-20" justification="center">
          <Typography translationKey="FCT.ACTIVITY_LOGS.END" />
        </Stack>
      )}
    </>
  );
}
